import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';

import { AiConsentState, WITHDRAWAL_ROUTE_LABELS } from '../../../core/attestations/ai-consent.types';

/**
 * The AI-processing consent, stated as one fact for Ops (FLOW-290).
 *
 * 🚨 **Belongs beside the documents, never in a risk block.** What it answers
 * is "may a fresh AI run be asked for on this file" — a permission the client
 * controls, not a finding about them. Rendered among the risk lens it would
 * read as a mark against the applicant, which withdrawing consent is not: it is
 * a right (POPIA s11(2)) and exercising it is neither suspicious nor adverse.
 *
 * ⚠️ **Three outcomes, not two.** Null means consent was never asked for on
 * this record, which is a different fact from withdrawn and must not be shown
 * as one — an Ops user reading "withdrawn" would go looking for an act that
 * never happened.
 *
 * 🚨 **FLOW-297: the business's consent, never rendered as "active".**
 * `UI-DA-60-04` stands — the product holds only its own record of the
 * client's position — so a standing consent reads *given, on this date*. A
 * withdrawal names its route and, when emailed, both moments; an open director
 * check is stated neutrally, never as suspicion.
 *
 * Carries no user identity and no reason, because the payload it renders
 * carries neither (`attestations.services.ai_consent_state`). "Recorded by Ops"
 * says only that the withdrawal arrived off-system — by phone or email — which
 * is what stops a reviewer hunting for a click nobody made.
 */
@Component({
  selector: 'app-ai-consent-fact',
  imports: [DatePipe],
  template: `
    <p class="ai-consent-fact" data-testid="ai-consent-fact">
      <span class="ai-consent-fact__label">AI consent (the business's)</span>
      @if (state(); as consent) {
        @if (consent.state === 'ACTIVE') {
          <span data-testid="ai-consent-fact-value">
            Given for the business on {{ consent.consented_at | date: 'medium' }}
            @if (consent.given_as === 'AGENT') {
              , by the introducing agent
            }
          </span>
        } @else {
          <span data-testid="ai-consent-fact-value">
            Withdrawn {{ routeLabel() }}, effective {{ consent.revoked_at | date: 'medium' }}
            @if (consent.recorded_by_ops) {
              ; recorded {{ consent.withdrawal_recorded_at | date: 'medium' }}
            }
          </span>
          @if (consent.director_check_pending) {
            <span data-testid="ai-consent-fact-check">Director check open</span>
          }
        }
      } @else {
        <span data-testid="ai-consent-fact-value">Never given</span>
      }
    </p>
  `,
  styleUrl: './ai-consent-fact.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiConsentFactComponent {
  readonly state = input<AiConsentState | null>(null);

  protected readonly routeLabel = computed(() => {
    const route = this.state()?.withdrawal_route;
    return route ? WITHDRAWAL_ROUTE_LABELS[route] : '';
  });
}
